/**
 * Procedural audio.
 *
 * Everything is synthesised with the Web Audio API — no asset files, no
 * licensing questions, and the engine note tracks the simulation exactly.
 *
 * Browsers refuse to start audio until a user gesture, so `unlock()` is called
 * from the first click or key press and everything before that is silent.
 */

import { clamp } from '@shared/math.js';

export type SurfaceSoundName = 'tarmac' | 'gravel' | 'dirt' | 'grass' | 'wet' | 'ice' | 'sand';

interface LoopNodes {
  source: AudioBufferSourceNode;
  gain: GainNode;
  filter: BiquadFilterNode;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private engineBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private engineOsc: OscillatorNode | null = null;
  private engineOsc2: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private turboOsc: OscillatorNode | null = null;
  private turboGain: GainNode | null = null;

  private surfaceLoop: LoopNodes | null = null;
  private skidLoop: LoopNodes | null = null;
  private windLoop: LoopNodes | null = null;

  private musicTimer: number | null = null;
  private musicStep = 0;

  masterVolume = 0.8;
  sfxVolume = 0.8;
  musicVolume = 0.45;
  muted = false;

  /** True once the context exists and is running. */
  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Creates/resumes the context. Safe to call on every interaction. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.buildGraph();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private buildGraph(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.masterVolume;
    this.master.connect(ctx.destination);

    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = this.sfxVolume;
    this.sfxBus.connect(this.master);

    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = this.musicVolume;
    this.musicBus.connect(this.master);

    this.engineBus = ctx.createGain();
    this.engineBus.gain.value = 0;
    this.engineBus.connect(this.sfxBus);

    // Shared white-noise buffer for tyres, gravel and crashes.
    const length = Math.floor(ctx.sampleRate * 2);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buffer;

    this.buildEngine();
    this.surfaceLoop = this.buildNoiseLoop(360, 0.5);
    this.skidLoop = this.buildNoiseLoop(1600, 0.2);
    this.windLoop = this.buildNoiseLoop(220, 0.1);
  }

  private buildEngine(): void {
    const ctx = this.ctx;
    if (!ctx || !this.engineBus) return;
    this.engineFilter = ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 900;
    this.engineFilter.Q.value = 6;

    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0.5;
    this.engineFilter.connect(this.engineGain);
    this.engineGain.connect(this.engineBus);

    this.engineOsc = ctx.createOscillator();
    this.engineOsc.type = 'sawtooth';
    this.engineOsc.frequency.value = 60;
    this.engineOsc.connect(this.engineFilter);
    this.engineOsc.start();

    this.engineOsc2 = ctx.createOscillator();
    this.engineOsc2.type = 'square';
    this.engineOsc2.frequency.value = 90;
    const osc2Gain = ctx.createGain();
    osc2Gain.gain.value = 0.35;
    this.engineOsc2.connect(osc2Gain);
    osc2Gain.connect(this.engineFilter);
    this.engineOsc2.start();

    // Turbo whine, only audible under boost.
    this.turboGain = ctx.createGain();
    this.turboGain.gain.value = 0;
    this.turboGain.connect(this.sfxBus!);
    this.turboOsc = ctx.createOscillator();
    this.turboOsc.type = 'triangle';
    this.turboOsc.frequency.value = 1200;
    this.turboOsc.connect(this.turboGain);
    this.turboOsc.start();
  }

  private buildNoiseLoop(frequency: number, q: number): LoopNodes | null {
    const ctx = this.ctx;
    if (!ctx || !this.noiseBuffer) return null;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = frequency;
    filter.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxBus!);
    source.start();
    return { source, gain, filter };
  }

  setVolumes(master: number, music: number, sfx: number, muted: boolean): void {
    this.masterVolume = master;
    this.musicVolume = music;
    this.sfxVolume = sfx;
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : master;
    if (this.musicBus) this.musicBus.gain.value = music;
    if (this.sfxBus) this.sfxBus.gain.value = sfx;
  }

  /** Called every frame while racing. */
  updateEngine(state: {
    rpm: number;
    throttle: number;
    speed: number;
    gear: number;
    boosting: boolean;
    slip: number;
    wheelSpin: number;
    surface: SurfaceSoundName;
    onRoad: boolean;
    active: boolean;
  }, dt: number): void {
    if (!this.ctx || !this.engineBus) return;
    if (!state.active) {
      this.engineBus.gain.value = 0;
      if (this.turboGain) this.turboGain.gain.value = 0;
      if (this.surfaceLoop) this.surfaceLoop.gain.gain.value = 0;
      if (this.skidLoop) this.skidLoop.gain.gain.value = 0;
      if (this.windLoop) this.windLoop.gain.gain.value = 0;
      return;
    }

    const rpmNorm = clamp(state.rpm / 7600, 0, 1);
    const base = 42 + rpmNorm * 210;
    const target = base * (0.96 + state.throttle * 0.08);
    const smoothing = clamp(dt * 12, 0, 1);
    if (this.engineOsc) {
      this.engineOsc.frequency.value += (target - this.engineOsc.frequency.value) * smoothing;
    }
    if (this.engineOsc2) {
      this.engineOsc2.frequency.value = (this.engineOsc?.frequency.value ?? target) * 1.5;
    }
    if (this.engineFilter) {
      this.engineFilter.frequency.value = 420 + rpmNorm * 2600 + state.throttle * 500;
    }
    const load = 0.25 + state.throttle * 0.55 + Math.abs(state.speed) / 900;
    this.engineBus.gain.value = clamp(load, 0, 1) * 0.32;

    if (this.turboGain && this.turboOsc) {
      this.turboGain.gain.value = state.boosting ? 0.06 : Math.max(0, this.turboGain.gain.value - dt * 0.3);
      this.turboOsc.frequency.value = 900 + rpmNorm * 1700;
    }

    // Tyre/surface noise.
    const surfaceGain = this.surfaceBase(state.surface) * clamp(Math.abs(state.speed) / 260, 0, 1);
    if (this.surfaceLoop) {
      const target2 = surfaceGain * 0.5;
      this.surfaceLoop.gain.gain.value += (target2 - this.surfaceLoop.gain.gain.value) * smoothing;
      this.surfaceLoop.filter.frequency.value = this.surfaceTone(state.surface);
    }
    if (this.skidLoop) {
      const squeal = clamp(state.slip * 1.1 + state.wheelSpin * 0.7, 0, 1) * clamp(Math.abs(state.speed) / 200, 0, 1);
      this.skidLoop.gain.gain.value += (squeal * 0.22 - this.skidLoop.gain.gain.value) * smoothing;
    }
    if (this.windLoop) {
      this.windLoop.gain.gain.value += (clamp(Math.abs(state.speed) / 520, 0, 1) * 0.06 - this.windLoop.gain.gain.value) * smoothing;
    }
  }

  private surfaceBase(sound: SurfaceSoundName): number {
    switch (sound) {
      case 'gravel':
        return 0.34;
      case 'dirt':
        return 0.26;
      case 'grass':
        return 0.22;
      case 'sand':
        return 0.3;
      case 'wet':
        return 0.24;
      case 'ice':
        return 0.08;
      default:
        return 0.16;
    }
  }

  private surfaceTone(sound: SurfaceSoundName): number {
    switch (sound) {
      case 'gravel':
        return 1500;
      case 'dirt':
        return 900;
      case 'grass':
        return 1100;
      case 'sand':
        return 700;
      case 'wet':
        return 2000;
      case 'ice':
        return 2600;
      default:
        return 900;
    }
  }

  /** One-shot sound effects. */
  sfx(name: 'crash' | 'boost' | 'countdown' | 'go' | 'lap' | 'finish' | 'click' | 'buy' | 'error' | 'unlock' | 'achievement', intensity = 1): void {
    if (!this.ctx || !this.sfxBus) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    switch (name) {
      case 'crash': {
        const noise = this.noiseSource(0.32);
        if (!noise) return;
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 900 + intensity * 900;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(clamp(0.35 * intensity, 0, 0.7), now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
        noise.connect(filter).connect(gain).connect(this.sfxBus);
        noise.start(now);
        noise.stop(now + 0.34);
        break;
      }
      case 'boost': {
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(1500, now + 0.3);
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.18, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
        osc.connect(gain).connect(this.sfxBus);
        osc.start(now);
        osc.stop(now + 0.36);
        break;
      }
      case 'countdown': {
        this.beep(520, 0.16, 0.22, 'square');
        break;
      }
      case 'go': {
        this.beep(1040, 0.34, 0.26, 'square');
        break;
      }
      case 'lap': {
        this.arp([660, 880], 0.1, 0.18);
        break;
      }
      case 'finish': {
        this.arp([523, 659, 784, 1047], 0.13, 0.2);
        break;
      }
      case 'click': {
        this.beep(660, 0.05, 0.09, 'square');
        break;
      }
      case 'buy': {
        this.arp([740, 990], 0.07, 0.14);
        break;
      }
      case 'error': {
        this.beep(160, 0.18, 0.16, 'sawtooth');
        break;
      }
      case 'unlock': {
        this.arp([660, 830, 1100], 0.09, 0.16);
        break;
      }
      case 'achievement': {
        this.arp([784, 988, 1175, 1568], 0.11, 0.18);
        break;
      }
    }
  }

  private noiseSource(duration: number): AudioBufferSourceNode | null {
    if (!this.ctx || !this.noiseBuffer) return null;
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    void duration;
    return source;
  }

  private beep(frequency: number, gain: number, duration: number, type: OscillatorType): void {
    const ctx = this.ctx;
    if (!ctx || !this.sfxBus) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = frequency;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.0005, now + duration);
    osc.connect(g).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  private arp(frequencies: number[], spacing: number, gain: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.sfxBus) return;
    const now = ctx.currentTime;
    frequencies.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = f;
      const g = ctx.createGain();
      const start = now + i * spacing;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.linearRampToValueAtTime(gain, start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0005, start + spacing * 1.6);
      osc.connect(g).connect(this.sfxBus!);
      osc.start(start);
      osc.stop(start + spacing * 1.8);
    });
  }

  /** Simple looping menu music: a slow arpeggio over a bass pulse. */
  startMusic(): void {
    if (!this.ctx || this.musicTimer !== null) return;
    const pattern = [220, 277, 330, 415, 330, 277, 247, 330];
    const bass = [110, 110, 98, 98, 123, 123, 110, 110];
    this.musicTimer = window.setInterval(() => {
      if (!this.ctx || !this.musicBus || this.ctx.state !== 'running') return;
      const step = this.musicStep++ % pattern.length;
      const now = this.ctx.currentTime;
      this.musicNote(pattern[step]!, now, 0.26, 0.05, 'triangle');
      if (step % 2 === 0) this.musicNote(bass[step]!, now, 0.42, 0.09, 'sawtooth');
    }, 260);
  }

  stopMusic(): void {
    if (this.musicTimer !== null) {
      window.clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
  }

  private musicNote(frequency: number, when: number, duration: number, gain: number, type: OscillatorType): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicBus) return;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = frequency;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1400;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.linearRampToValueAtTime(gain, when + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0005, when + duration);
    osc.connect(filter).connect(g).connect(this.musicBus);
    osc.start(when);
    osc.stop(when + duration + 0.05);
  }

  /** Silences the continuous loops (used when leaving a race or hiding the tab). */
  silence(): void {
    if (this.engineBus) this.engineBus.gain.value = 0;
    if (this.turboGain) this.turboGain.gain.value = 0;
    if (this.surfaceLoop) this.surfaceLoop.gain.gain.value = 0;
    if (this.skidLoop) this.skidLoop.gain.gain.value = 0;
    if (this.windLoop) this.windLoop.gain.gain.value = 0;
  }

  dispose(): void {
    this.stopMusic();
    this.silence();
    if (this.ctx) {
      try {
        void this.ctx.close();
      } catch {
        /* already closed */
      }
      this.ctx = null;
    }
  }
}
